import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import './ValidationPage.css';

/**
 * ValidationPage is a React component responsible for managing and displaying the status of a job's validation process. It periodically checks
 * the progress of the validation job by polling an API, updates the UI based on the job's status, and navigates the user based on the results.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.jobId - The unique identifier for the validation job.
 * @param {Function} props.setValidationData - A callback function used to store the validation results.
 * @return {JSX.Element} The rendered ValidationPage component.
 */
function ValidationPage({ jobId, setValidationData }) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Starting validation...');
  const [errors, setErrors] = useState([]);

  // Use ref to track completion synchronously (state updates are async)
  const isCompletedRef = useRef(false);

  useEffect(() => {
    if (!jobId) {
      navigate('/');
      return;
    }

    // Reset on mount
    isCompletedRef.current = false;
    let intervalId = null;

    const checkProgress = async () => {
      // Prevent duplicate processing - check ref synchronously
      if (isCompletedRef.current) {
        return;
      }

      try {
        const response = await apiService.getValidationProgress(jobId);
        const data = response.data;

        setProgress(Math.round((data.progress / data.total) * 100));
        setStatus(data.status);
        setMessage(data.message);
        setErrors(data.errors || []);

        if (data.status === 'completed') {
          // Mark as completed IMMEDIATELY and synchronously
          if (isCompletedRef.current) {
            return; // Double-check in case of race
          }
          isCompletedRef.current = true;

          // Stop polling
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }

          // Save validation data with full results for ThnksSelectionPage
          setValidationData({
            ...data,
            results: data.results || []
          });

          if (data.errors && data.errors.length > 0) {
            navigate('/errors');
          } else {
            // Navigate to Thnks selection page
            navigate('/select-thnks');
          }
        } else if (data.status === 'failed') {
          setMessage('Validation failed. Please try again.');
        }
      } catch (error) {
        console.error('Error checking progress:', error);
        setMessage('Error checking validation progress');
      }
    };

    // Check progress immediately
    checkProgress();

    // Then check every 2 seconds
    intervalId = setInterval(checkProgress, 2000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [jobId, navigate, setValidationData]);

  const handleCancel = () => {
    navigate('/');
  };

  return (
    <div className="validation-page">
      <div className="validation-container">
        <div className="validation-card">
          <h1>Validating Addresses</h1>

          <div className="progress-section">
            <div className="progress-bar-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="progress-text">{progress}%</span>
            </div>

            <p className="status-message">{message}</p>
          </div>

          {status === 'processing' && (
            <div className="spinner-container">
              <div className="spinner"></div>
              <p>Please wait while we validate your addresses...</p>
            </div>
          )}

          {errors.length > 0 && (
            <div className="error-preview">
              <h3>Issues Found: {errors.length}</h3>
              <p className="error-info">
                We'll help you correct these addresses after validation completes.
              </p>
            </div>
          )}

          <div className="action-buttons">
            <button
              onClick={handleCancel}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ValidationPage;